import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";

export interface AccordionProps extends ComponentPropsWithRef<"div"> {}

/**
 * Conteneur de sections repliables.
 *
 * Chaque section est un `<details>` natif : ouverture/fermeture, clavier,
 * recherche dans la page (Ctrl+F ouvre la section correspondante) et
 * accessibilité sont fournis par le navigateur. Une implémentation en `useState`
 * perdrait les deux dernières.
 */
export function Accordion({ className, ...props }: AccordionProps) {
  return (
    <div
      className={cn("divide-y divide-border overflow-hidden rounded-xl border border-border", className)}
      {...props}
    />
  );
}

export interface AccordionItemProps extends Omit<ComponentPropsWithRef<"details">, "title"> {
  title: ReactNode;
  /** Nom de groupe : deux `<details>` partageant un `name` s'excluent mutuellement. */
  name?: string;
}

export function AccordionItem({
  className,
  title,
  children,
  name,
  ...props
}: AccordionItemProps) {
  return (
    // `data-nexty-accordion` déclenche l'animation d'ouverture définie dans
    // @one-nexty/tailwind-config : le contenu d'un <details> ne peut pas être animé
    // par des classes utilitaires, il faut une règle CSS sur [open].
    <details
      data-nexty-accordion=""
      className={cn("group bg-surface-raised", className)}
      name={name}
      {...props}
    >
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4",
          "text-sm font-medium text-fg transition-colors duration-fast ease-standard hover:bg-surface-sunken",
          "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
          // Retire le triangle natif, y compris sur Safari.
          "[&::-webkit-details-marker]:hidden",
        )}
      >
        {title}
        <svg
          className="size-4 shrink-0 text-fg-subtle transform-gpu transition-transform duration-slow ease-standard group-open:rotate-180"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 8 4 4 4-4" />
        </svg>
      </summary>

      <div className="px-5 pb-4 text-sm text-fg-muted">{children}</div>
    </details>
  );
}
